// context/Admin/SuppliersContext.js
import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import api from "../../api/api"; // Axios instance for API calls
import { PaginationInfo } from "../../types/api";

interface SuppliersContextType {
  suppliers: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchSuppliers: (page?: number, perPage?: number) => Promise<void>;
  addSupplier: (supplier: any) => Promise<void>;
  editSupplier: (updatedSupplier: any) => Promise<void>;
  deleteSupplier: (id: number | string) => Promise<void>;
}

export const SuppliersContext = createContext<SuppliersContextType | undefined>(
  undefined,
);

interface SuppliersProviderProps {
  children: ReactNode;
}

export const useSuppliersContext = (): SuppliersContextType => {
  const context = useContext(SuppliersContext);
  if (!context) {
    throw new Error("useSuppliersContext must be used within SuppliersProvider");
  }
  return context;
};

const SuppliersProvider: React.FC<SuppliersProviderProps> = ({ children }) => {
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch suppliers from API with pagination
  const fetchSuppliers = useCallback(async (page: number = 1, perPage: number = 15) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/suppliers?${params.toString()}`);
      
      // Handle paginated response structure
      let suppliersData = [];
      let paginationData = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          suppliersData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          suppliersData = responseData.data;
          paginationData = responseData.pagination;
        }
      } else if (Array.isArray(responseData)) {
        suppliersData = responseData;
      }
      
      setSuppliers(suppliersData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch suppliers:", error);
      setSuppliers([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new supplier
  const addSupplier = async (supplier: any): Promise<void> => {
    try {
      const response = await api.post("/suppliers", supplier);
      const created = response.data?.data || response.data;
      setSuppliers((prev) => [...prev, created]);
    } catch (error) {
      console.error("Failed to add supplier:", error);
      throw error;
    }
  };

  // Edit an existing supplier
  const editSupplier = async (updatedSupplier: any): Promise<void> => {
    try {
      const response = await api.put(
        `/suppliers/${updatedSupplier.id}`,
        updatedSupplier
      );
      const updated = response.data?.data || response.data;
      setSuppliers((prev) =>
        prev.map((supplier) =>
          supplier.id === updatedSupplier.id ? updated : supplier
        )
      );
    } catch (error) {
      console.error("Failed to edit supplier:", error);
      throw error;
    }
  };

  // Delete a supplier
  const deleteSupplier = async (id: number | string): Promise<void> => {
    try {
      await api.delete(`/suppliers/${id}`);
      setSuppliers((prev) => prev.filter((supplier) => supplier.id !== id));
    } catch (error) {
      console.error("Failed to delete supplier:", error);
      throw error;
    }
  };

  // REMOVED auto-fetch useEffect - components will call fetchSuppliers explicitly with pagination

  return (
    <SuppliersContext.Provider
      value={{
        suppliers,
        pagination,
        loading,
        fetchSuppliers,
        addSupplier,
        editSupplier,
        deleteSupplier,
      }}
    >
      {children}
    </SuppliersContext.Provider>
  );
};

export default SuppliersProvider;
